import type { LocationQuery } from 'vue-router';
import type {
  ComparisonFiltersData,
  ComparisonFiltersInput,
  ComparisonFilterTemplate,
} from '~/graphql/generated/schema-types';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { EventBus } from '~/composables/bus';
import { ComparisonSort, SalesPeriod } from '~/graphql/generated/schema-types';
import { getComparisonFilters } from '~/utils/api';

export const useComparisonStore = defineStore('comparisonStore', () => {
  const route = useRoute();

  const filters = ref<ComparisonFiltersData>({} as ComparisonFiltersData);
  const activeFilters = ref<ComparisonFiltersInput>({
    categories: [],
    collections: [],
    exteriors: [],
    liked: null,
    hided: false,
    markets_from: [],
    markets_to: [],
    sales_period: SalesPeriod.Monthly,
    offers: null,
    price_from: null,
    price_to: null,
    profit_percentage_from: null,
    profit_percentage_to: null,
    profit_value_from: null,
    profit_value_to: null,
    rarities: [],
    sales: null,
    search: null,
    steam_price_from: null,
    steam_price_to: null,
    types: [],
  } as ComparisonFiltersInput);
  const page = ref(+(route.query.page || 1));
  const sort = ref(route.query.sort as ComparisonSort || ComparisonSort.SalesDesc);
  const presets = ref<ComparisonFilterTemplate[]>([]);

  const isValidKey = (key: string) => {
    const arr = ['page', 'sort', 'lang'];
    if (arr.includes(key)) {
      return true;
    }
    return key in activeFilters.value;
  };

  const initializeFilters = () => {
    const parsedFilters = parseQueryParams(route.query as any);
    const bool = Object.keys(parsedFilters).some(key => !isValidKey(key));

    if (bool) {
      return;
    }

    if (parsedFilters.page) {
      page.value = parsedFilters.page;
      delete parsedFilters.page;
    }
    if (parsedFilters.sort) {
      sort.value = parsedFilters.sort;
      delete parsedFilters.sort;
    }
    activeFilters.value = {
      ...activeFilters.value,
      ...parsedFilters,
    };
  };

  const fetchCompareFilters = async () => {
    if (Object.keys(filters.value)?.length) {
      return;
    }

    try {
      const { data, errors } = await getComparisonFilters();
      if (errors?.length) {
        throw new Error(errors[0]?.message);
      }

      filters.value = data?.comparePageFilters || null;
    } catch (e: any) {
      console.log(e.message);
      EventBus.$emit('notify', {
        success: false,
        msg: e.message || 'An error occurred',
      });
      console.log('getComparisonFilters() error');
    }
  };

  function parseQueryParams(query: LocationQuery) {
    const result: Partial<ComparisonFiltersInput> = {};
    for (const key in query) {
      try {
        const decodedKey = (decodeURIComponent(key) || key) as keyof ComparisonFiltersInput;
        result[decodedKey] = JSON.parse(decodeURIComponent(query[decodedKey] as string));
      } catch {
        if (!Number.isNaN(Number(query[key]))) {
          // @ts-ignore
          result[key] = Number(query[key]);
        } else if (query[key] === 'true' || query[key] === 'false') {
          // @ts-ignore
          result[key] = query[key] === 'true';
        } else {
          // @ts-ignore
          result[key] = query[key];
        }
      }
    }

    return result as ComparisonFiltersInput & { page?: number, sort?: ComparisonSort };
  }

  const getClearedFilters = () => {
    return {
      categories: [],
      collections: [],
      exteriors: [],
      liked: null,
      markets_from: [],
      markets_to: [],
      sales_period: SalesPeriod.Weekly,
      offers: null,
      price_from: null,
      price_to: null,
      profit_percentage_from: null,
      profit_percentage_to: null,
      profit_value_from: null,
      profit_value_to: null,
      rarities: [],
      sales: null,
      search: null,
      steam_price_from: null,
      steam_price_to: null,
      types: [],
    };
  };

  return {
    filters,
    activeFilters,
    page,
    sort,
    presets,
    getClearedFilters,
    initializeFilters,
    fetchCompareFilters,
  };
});
